import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Activity, Hostage } from "../types";
import { Typography } from "@mui/material";
import ActivityList from "../Activity/ActivityList";
import { useUser } from "../../context/UserContext";

interface UserEventsProps {
  allHostages: Hostage[];
}

const UserEvents: React.FC<UserEventsProps> = ({ allHostages: userHostages }) => {
  const { t, i18n } = useTranslation();
  const { user, fetchUser } = useUser();
  
  // State to hold registered activities
  const [registeredActivities, setRegisteredActivities] = useState<Activity[]>(user?.registered_activities ?? []);
  const [registeredActivitiesHe, setRegisteredActivitiesHe] = useState<Activity[]>(user?.registered_activities ?? []);
  const [registeredActivitiesEn, setRegisteredActivitiesEn] = useState<Activity[]>(user?.registered_activities ?? []);
  const [nonRegisteredActivities, setNonRegisteredActivities] = useState<Activity[]>([]);
  const [nonRegisteredActivitiesHe, setNonRegisteredActivitiesHe] = useState<Activity[]>([]);
  const [nonRegisteredActivitiesEn, setNonRegisteredActivitiesEn] = useState<Activity[]>([]);
  const [registeredUsersMap, setRegisteredUsersMap] = useState<Record<string, number>>({});

  useEffect(() => {
    const fetchAll = async () => {
      const fetchedActivities = await fetchAllNonRegActivities();
    };
    fetchAll();
  }, []);

  useEffect(() => {
    setNonRegisteredActivitiesHe(nonRegisteredActivities.filter(x=>!!x?.he?.title));
    setNonRegisteredActivitiesEn(nonRegisteredActivities.filter(x=>!!x?.en?.title));

  }, [nonRegisteredActivities]);

  useEffect(() => {
    setRegisteredActivitiesHe(registeredActivities.filter(x=>!!x?.he?.title));
    setRegisteredActivitiesEn(registeredActivities.filter(x=>!!x?.en?.title));

  }, [registeredActivities]);


  const initNumOfRegisteredUsers = (nonRegisteredActivities: Activity[]) => {
    const userCountMap: Record<string, number> = {};

    [...registeredActivities, ...nonRegisteredActivities].forEach((activity) => {
      if (activity.id) {
        userCountMap[activity.id] = activity.registeredUsersCount;
      }
    });

    setRegisteredUsersMap(userCountMap);
  }

  async function fetchAllNonRegActivities() {
    console.log('fetching all user NonRegistered activities...');  
    try {
      const response = await fetch("/api/user/nonRegActivities", {
        method: 'GET',
        credentials: 'include' 
      });
  
      if (response.ok) {
        const data = await response.json() as Activity[];
        
        setNonRegisteredActivities(data);
        initNumOfRegisteredUsers(data);        
        
        return data;
      } else {
        console.error(`Failed to fetch: ${response.status}`);
        return null;
      }
    } catch (error) {
      console.error('Error fetching All-User-NonReg-Activities data:', error);
      return null;
    }
  };

  const handleJoin = (activityId: string) => {
    const registeredActivity = nonRegisteredActivities.find(activity => activity.id === activityId);
    if (registeredActivity) {
      setNonRegisteredActivities(prev => prev.filter(activity => activity.id !== activityId));
      setRegisteredActivities(prev => [...prev, registeredActivity]);

      setRegisteredUsersMap(prev => ({
        ...prev,
        [activityId]: (prev[activityId] || 0) + 1, // Increment the count, defaulting to 0 if undefined
      }));
    }
  };
  
  const handleUndoJoin = (activityId: string) => {
    const deregisteredActivity = registeredActivities.find(activity => activity.id === activityId);
    if (deregisteredActivity) {
      setRegisteredActivities(prev => prev.filter(activity => activity.id !== activityId));
      setNonRegisteredActivities(prev => [...prev, deregisteredActivity]);

      setRegisteredUsersMap(prev => ({
        ...prev,
        [activityId]: (prev[activityId] || 1) - 1, // Increment the count, defaulting to 1 if undefined, so the result is 0
      }));
    }
  };  

  const handleRefresh = async () => {
    const updatedUser = await fetchUser(); // Fetch user data and update context
    setRegisteredActivities(updatedUser.registered_activities); // Update registered activities state
    await fetchAllNonRegActivities();
  };

  const renderRegisteredActivities = () => {
    if (!registeredActivities || registeredActivities.length === 0) return null;
    return (
      <>
        <Typography
          variant="subtitle1"
          component="h2"
          className="user-activities-subtitle">
          {t("userHome.activitiesListTitle")}
        </Typography>
        <ActivityList
          activities={i18n.language == "he" ? registeredActivitiesHe : registeredActivitiesEn}
          registeredUsersMap = {registeredUsersMap}
          hostages={userHostages}
          showLink={true}
          onRefresh={handleRefresh}
          onJoin={handleJoin}
          onUndoJoin={handleUndoJoin}
        />
      </>
    );
  };

  if (!user) {
    return null;
  }

  return (
    <div>
      {renderRegisteredActivities()}
      <div className="additional-activities-container">
        <Typography
          variant="subtitle1"
          component="h2"
          className="user-activities-subtitle">
          {t("userHome.additionalActivitiesListTitle")}
        </Typography>
        <ActivityList
          activities={i18n.language == "he" ? nonRegisteredActivitiesHe : nonRegisteredActivitiesEn} 
          hostages={userHostages} 
          showLink={true} 
          registeredUsersMap = {registeredUsersMap}
          onRefresh={handleRefresh}
          onJoin={handleJoin}
          onUndoJoin={handleUndoJoin}
        />
      </div>
    </div>
  );
};

export default UserEvents;
